import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { SizeOption } from '../../types';
import { getMakerItems } from './MakerItems.constant';
import { FI_MAKERS_ANCHOR } from '../../AppPaths';
import * as types from '../../types';
import { Series } from '../../types/Series';
import styles from './Accessories.module.scss';
import { ModuleItem } from './AccessoryTypes';
import AccessoryGroup from './components/AccessoryGroup/AccessoryGroup';
import {
  collarProductIsSupportedInCountry,
  makersAccessoriesProductIsSupportedInCountry,
  useLocale,
} from '../../contexts/LocaleContext';

export type SeriesProducts = Record<Series, ModuleItem[]>;

function useFindProducts() {
  const products = useSelector((state: types.AppState) => state.config.products);

  return useCallback(
    (
      descriptors: Array<{
        id: string;
        nameOverride?: string;
      }>,
    ): ModuleItem[] => {
      const items: ModuleItem[] = [];

      // Make sure to return the products in the same order as the `names` array was passed in.
      for (const { id, nameOverride } of descriptors) {
        const product: types.IProduct | undefined = products.find((p) => p.id === id);
        if (!product) {
          continue;
        }

        if (product.category === types.ProductCategory.BAND) {
          // We only need 1 per color/type so we'll grab the medium size variant by default.
          const variants = product.variants.filter((v) => v.options.size === SizeOption.Medium);
          for (const variant of variants) {
            items.push({
              productId: product.id,
              productName: product.name,
              category: product.category,
              priceInCents: product.priceInCents,
              pricingInCents: product.pricingInCents,
              variantOptions: variant.options,
              bandSeries: product.bandSeries,
              nameOverride,
            });
          }
        } else if (product.category === types.ProductCategory.CHARGING_BASE) {
          items.push({
            productId: product.id,
            productName: product.name,
            category: product.category,
            priceInCents: product.priceInCents,
            pricingInCents: product.pricingInCents,
            nameOverride,
          });
        }
      }

      return items;
    },
    [products],
  );
}

function Accessories() {
  const { t } = useTranslation();
  const findProducts = useFindProducts();

  const s2Items = useMemo((): ModuleItem[] => {
    return findProducts([
      { id: 'additional-band' },
      {
        id: 'martingale-band',
        nameOverride: t('series_2_martingale_band'),
      },
      { id: 'additional-base' },
    ]);
  }, [findProducts, t]);

  const s3Items = useMemo((): ModuleItem[] => {
    return findProducts([{ id: 'additional-band-s3' }, { id: 'additional-base-s3' }]);
  }, [findProducts]);

  const allItems: SeriesProducts = {
    [Series.Series2]: s2Items,
    [Series.Series3]: s3Items,
  };

  const makerHeaderLink = (
    <a href="https://fidogs.typeform.com/to/X7D3REtB?typeform-source=admin.typeform.com">{t('become_a_fi_maker')}</a>
  );
  const { country } = useLocale();
  const makerItems = useMemo(() => getMakerItems(t, country), [t, country]);
  const showMakerSection = makerItems.length > 0 && makersAccessoriesProductIsSupportedInCountry(country);

  return (
    <div className={styles.container}>
      <div className={styles.accessoriesModule}>
        <h2>{t('accessories_uppercase')}</h2>
        <AccessoryGroup
          enableSeriesToggle={collarProductIsSupportedInCountry(types.series2CollarId, country)}
          items={allItems}
        />
        {showMakerSection && (
          <>
            <hr />
            <div id={FI_MAKERS_ANCHOR} className={styles.fiMakersSection}>
              <AccessoryGroup
                title={t('fi_makers')}
                subtitle={t('approved_third_party_accessories')}
                headerLink={makerHeaderLink}
                makerItems={makerItems}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Accessories;
